import dayjs from "dayjs";
import { generatePath, useOutletContext } from "react-router";
import { styled } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { PageLayout } from "@acto/taskpane/field-sales-2/components/patterns/PageLayout";
import { Card, RowList } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { CustomerRevenueCard } from "@acto/taskpane/field-sales-2/components/primitives/CustomerRevenueCard";
import { GlassButton } from "@acto/taskpane/field-sales-2/components/primitives/GlassButton";
import { RecapEntryBanner } from "@acto/taskpane/field-sales-2/components/patterns/recap/RecapEntryBanner";
import { MeetingPrepEntryBanner } from "@acto/taskpane/field-sales-2/components/patterns/MeetingPrepEntryBanner";
import { NudgeBanner } from "@acto/taskpane/field-sales-2/components/patterns/NudgeBanner";
import { useCustomerRevenueTrend } from "@acto/taskpane/field-sales-2/data-access/useCustomerRevenueTrend";
import { useCorporateGroupSummary } from "@acto/taskpane/field-sales-2/data-access/useCorporateGroupSummary";
import { useIsFromGroupView, useNavigateWithReturn } from "@acto/taskpane/field-sales-2/navigation";
import type { CustomerOutletContext } from "@acto/taskpane/field-sales-2/CustomerDetailsLayout";
import { useProductDrill } from "@acto/taskpane/field-sales-2/pages/analysis/ProductDrillContext";
import { useNotes } from "@acto/taskpane/core/data-access";
import { toIsoDate, trendToRevenueSnapshot } from "@acto/taskpane/core/chat-envelope";
import { useContributeCustomerDetailScope } from "@acto/taskpane/field-sales-2/utils/useContributeCustomerDetailScope";
import { CorporateGroupHint } from "./CorporateGroupHint";
import { KpiRow } from "./KpiRow";
import { Kpi } from "@acto/taskpane/core/domain";
import { useOrdersInRevenueDetailEnabled } from "@acto/taskpane/field-sales-2/data-access/useOrdersInRevenueDetailEnabled";
import { UpcomingMeetings } from "./UpcomingMeetings";

const OverviewCard = styled(Card)(({ theme }) => ({
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[20]}`,
}));

const OverviewRowList = styled(RowList)(({ theme }) => ({
  gap: theme.fieldSalesTwo.spacing[16],
}));

const RevenueDetailsButton = styled(GlassButton)(({ theme }) => ({
  width: "100%",
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

export function CustomerOverview() {
  const push = useNavigateWithReturn();
  const t = useTranslations("fieldSalesTwo");
  const { locale } = useLocale();

  const { customer, isPending, prepStoryHeadline, selection, onKpiChange } = useOutletContext<CustomerOutletContext>();
  const { drillTo } = useProductDrill();
  const isOrdersViewEnabled = useOrdersInRevenueDetailEnabled();
  const isFromGroupView = useIsFromGroupView();
  const trend = useCustomerRevenueTrend({ clientId: customer?.id });
  const lastNote = useNotes({ clientId: customer?.id, limit: 1 });
  const { group } = useCorporateGroupSummary(customer?.id);

  // Ambient context: the overview tab's rendered values (upcoming meetings and the
  // open-visits count contribute from their own components).
  const overviewFragment = {
    next_meeting_story_headline: prepStoryHeadline,
    ...trendToRevenueSnapshot(trend.raw, selection),
    last_order_date: toIsoDate(customer?.lastOrder?.date),
    last_offer_date: toIsoDate(customer?.lastOffer?.validFrom),
    last_recap_date: toIsoDate(lastNote.data?.[0]?.createdAt),
  };
  useContributeCustomerDetailScope("overview", overviewFragment);

  const handleMoreDetailsClick = () => {
    if (!customer) return;
    push(generatePath(ActoRoutes.CUSTOMER_ANALYSIS, { id: customer.id }));
  };

  const handleOpenOrders = () => {
    if (!customer) return;
    if (isOrdersViewEnabled) {
      onKpiChange(Kpi.ORDERS, { ordersList: "orders" });
      drillTo(0);
      push(generatePath(ActoRoutes.CUSTOMER_ANALYSIS, { id: customer.id }));
      return;
    }
    push(generatePath(ActoRoutes.CUSTOMER_ORDERS, { id: customer.id }));
  };

  const handleOpenOffers = () => {
    if (!customer) return;
    push(generatePath(ActoRoutes.CUSTOMER_OFFERS, { id: customer.id }));
  };

  const handleOpenRecaps = () => {
    if (!customer) return;
    push(generatePath(ActoRoutes.CUSTOMER_RECAPS, { id: customer.id }));
  };

  const handleOpenGroup = () => {
    if (!customer) return;
    push(generatePath(ActoRoutes.CUSTOMER_GROUP, { id: customer.id }));
  };

  // Only a real name personalizes the group entry points; an external reference alone
  // reads as noise, so it falls back to the generic copy.
  const groupName = group?.name ?? null;
  const canOpenGroup = !!group && !isFromGroupView;
  const groupHint = group ? (
    <CorporateGroupHint
      label={groupName ? t("corporateGroup.partOfNamedGroup", { name: groupName }) : t("corporateGroup.partOfGroup")}
      onOpenGroup={canOpenGroup ? handleOpenGroup : undefined}
    />
  ) : undefined;

  const lastOrderDate = customer?.lastOrder?.date;
  const lastOfferDate = customer?.lastOffer?.validFrom;
  const overviewRows = [
    { title: t("orders"), date: lastOrderDate ? dayjs(lastOrderDate) : undefined, onClick: handleOpenOrders },
    {
      title: t("offers"),
      date: lastOfferDate ? dayjs(lastOfferDate) : undefined,
      onClick: lastOfferDate ? handleOpenOffers : undefined,
    },
    {
      title: t("visitReports"),
      date: lastNote?.data?.[0]?.createdAt,
      onClick: lastNote?.data?.[0] ? handleOpenRecaps : undefined,
    },
  ];

  return (
    <PageLayout hPadding="card" isLoading={isPending || trend.isPending} isEmpty={!customer && !isPending}>
      {!customer ? (
        <>{t("customerNotFound")}</>
      ) : (
        <>
          <RecapEntryBanner clientId={customer.id} contributeTo="customer-detail" />

          <MeetingPrepEntryBanner clientId={customer.id} storyHeadline={prepStoryHeadline} />

          <UpcomingMeetings clientId={customer.id} street={customer.street} city={customer.city} />

          <CustomerRevenueCard
            headerSlot={groupHint}
            externalReference={customer.externalReference}
            name={customer.name}
            trend={trend}
            footer={<RevenueDetailsButton onClick={handleMoreDetailsClick}>{t("revenueDetails")}</RevenueDetailsButton>}
          />

          {canOpenGroup && (
            <NudgeBanner
              label={groupName ?? t("corporateGroup.bannerFallbackTitle")}
              subLabel={groupName ? t("corporateGroup.bannerSubtitle") : undefined}
              onClick={handleOpenGroup}
            />
          )}

          <OverviewCard>
            <OverviewRowList role="list">
              {overviewRows.map((row) => (
                <KpiRow key={row.title} title={row.title} date={row.date} locale={locale} onClick={row.onClick} />
              ))}
            </OverviewRowList>
          </OverviewCard>
        </>
      )}
    </PageLayout>
  );
}
